/**
 * Time Control System - Type Definitions
 *
 * Defines all types and interfaces for the time control system.
 * Supports local (PvP, PvAI) and multiplayer game modes.
 */

/**
 * Available time control presets
 */
export enum TimeControlPreset {
  OFF = 'off',           // No time limit
  SPRINT = 'sprint',     // 1 minute + 2 second increment
  STANDARD = 'standard', // 2 minutes + 3 second increment
  MARATHON = 'marathon', // 6 minutes + 5 second increment
  ANY = 'any'           // Matchmaking only: accept any time control
}

/**
 * Time control configuration
 */
export interface TimeControlConfig {
  preset: TimeControlPreset;
  initialTime: number;      // milliseconds
  increment: number;         // milliseconds added per move
}

/**
 * Player-specific time state
 */
export interface PlayerTimeState {
  lightTimeRemaining: number;    // milliseconds
  darkTimeRemaining: number;     // milliseconds
  lightLastMoveTime?: number;    // timestamp of last move (for multiplayer sync)
  darkLastMoveTime?: number;     // timestamp of last move (for multiplayer sync)
  activePlayer: 'light' | 'dark' | null;
  isPaused: boolean;
}

/**
 * Complete time control state
 */
export interface TimeControlState {
  config: TimeControlConfig;
  playerTime: PlayerTimeState;
  timeExpired: boolean;
  winner?: 'light' | 'dark';     // Set when time expires
}

/**
 * Time control mode (affects sync behavior)
 */
export type TimeControlMode = 'local' | 'multiplayer';

/**
 * Time update event (for UI subscriptions)
 */
export interface TimeUpdateEvent {
  lightTimeRemaining: number;
  darkTimeRemaining: number;
  activePlayer: 'light' | 'dark' | null;
  isPaused: boolean;
  timeExpired: boolean;
  winner?: 'light' | 'dark';
}

/**
 * Multiplayer sync payload
 */
export interface TimeControlSyncData {
  lightTimeRemaining: number;
  darkTimeRemaining: number;
  lastMoveTimestamp: number;     // Server timestamp
  activePlayer: 'light' | 'dark';
}
